import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Banner } from '@lumal/ui';
import { apiFetch } from '@lumal/lib';
import { API } from '../../api';
import { btnPrimary, btnSecondary, btnDanger, tbl, formStyle, statCard, statNumber, badge, drawer } from '../../styles';
import type { MeResponse, Plan, TenantView, TenantBranding, LicenseResult, TenantBranchRow, TenantUserRow, TenantUsage } from '../../types';
import { has } from '../../types';

// PlatformPanel: operación de la plataforma (stakeholder Lumal). Aprovisiona y
// licencia clientes, ve métricas cross-tenant, y opera el ciclo de vida de cada
// cliente (detalle, branding white-label, suspender/reactivar/terminar, cambiar
// plan). Consume ops-bff /portal/platform/*. Ver licensing-and-provisioning.md.
export function PlatformPanel({ me, csrf }: { me: MeResponse; csrf: string }) {
  const qc = useQueryClient();
  const canProvision = has(me, 'platform:tenants');
  const canLicense = has(me, 'platform:licensing');
  const canBranding = has(me, 'platform:branding');
  const [selected, setSelected] = useState<TenantView | null>(null);

  const metrics = useQuery<{ tenants_total: number; tenants_active: number; plans: number; outbox_pending: number }>({
    queryKey: ['pf-metrics'], queryFn: () => apiFetch(`${API}/portal/platform/metrics`),
  });
  const tenants = useQuery<{ items: TenantView[] }>({
    queryKey: ['pf-tenants'], queryFn: () => apiFetch(`${API}/portal/platform/tenants`),
  });
  const plans = useQuery<{ items: Plan[] }>({
    queryKey: ['pf-plans'], queryFn: () => apiFetch(`${API}/portal/platform/plans`),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['pf-tenants'] });
    qc.invalidateQueries({ queryKey: ['pf-metrics'] });
  };

  const provision = useMutation({
    mutationFn: (body: any) => apiFetch(`${API}/portal/platform/tenants`, 'POST', { headers: { 'X-CSRF-Token': csrf }, body }),
    onSuccess: invalidate,
  });

  const [form, setForm] = useState({ slug: '', display_name: '', plan_code: 'starter', primary_hex: '#3a4a90' });
  const [err, setErr] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(null);
    try {
      await provision.mutateAsync({ slug: form.slug, display_name: form.display_name, plan_code: form.plan_code, branding: { primary_hex: form.primary_hex } });
      setForm({ ...form, slug: '', display_name: '' });
    } catch (er: any) { setErr(er.message_safe ?? 'Error'); }
  };

  const m = metrics.data;
  return (
    <section data-testid="platform-panel" style={{ marginTop: '1rem' }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Stat label="Clientes" value={m?.tenants_total} testid="stat-tenants" />
        <Stat label="Activos" value={m?.tenants_active} testid="stat-active" />
        <Stat label="Planes" value={m?.plans} testid="stat-plans" />
        <Stat label="Outbox pendiente" value={m?.outbox_pending} testid="stat-outbox" />
      </div>

      {canProvision && (
        <details style={{ marginBottom: 12 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>+ Aprovisionar cliente</summary>
          <form onSubmit={submit} style={formStyle} data-testid="provision-form">
            <input placeholder="slug (ej. clinica-sur)" value={form.slug} required
              onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="pf-slug" />
            <input placeholder="Nombre comercial" value={form.display_name} required
              onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="pf-name" />
            <select value={form.plan_code} onChange={(e) => setForm({ ...form, plan_code: e.target.value })} data-testid="pf-plan">
              {(plans.data?.items || []).map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
            </select>
            <input type="color" value={form.primary_hex} onChange={(e) => setForm({ ...form, primary_hex: e.target.value })} data-testid="pf-color" />
            <button type="submit" style={btnPrimary} disabled={provision.isPending} data-testid="pf-submit">Aprovisionar</button>
            {err && <Banner variant="error">{err}</Banner>}
          </form>
        </details>
      )}

      <h3>Clientes</h3>
      <table style={tbl} data-testid="tenants-table">
        <thead>
          <tr><th>Cliente</th><th>Plan</th><th>Estado</th><th>Tier</th><th>Sucursales</th><th>Módulos</th><th>Acciones</th></tr>
        </thead>
        <tbody>
          {(tenants.data?.items || []).map((t) => (
            <tr key={t.id} data-testid={`tenant-${t.slug}`}
              style={selected?.id === t.id ? { outline: '2px solid var(--lumal-primary)' } : undefined}>
              <td><strong>{t.brand_name}</strong><br /><small>{t.slug}</small></td>
              <td>{t.plan_name || '—'}</td>
              <td><span style={statusStyle(t.license_status)} data-testid={`tenant-status-${t.slug}`}>{t.license_status}</span></td>
              <td>{t.availability_tier || '—'}</td>
              <td>{t.branch_count}</td>
              <td><span data-testid={`tenant-modules-${t.slug}`}>{Object.keys(t.features).length}</span></td>
              <td>
                <button style={btnSecondary} data-testid={`tenant-detail-${t.slug}`}
                  onClick={() => setSelected(selected?.id === t.id ? null : t)}>
                  {selected?.id === t.id ? 'Cerrar' : 'Detalle'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {selected && (
        <TenantDetail
          key={selected.id}
          tenant={selected}
          plans={plans.data?.items || []}
          csrf={csrf}
          canLicense={canLicense}
          canBranding={canBranding}
          canStatus={canProvision}
          onChanged={invalidate}
          onClose={() => setSelected(null)}
        />
      )}

      <details style={{ marginTop: 16 }}>
        <summary style={{ cursor: 'pointer' }}>Catálogo de planes</summary>
        <table style={tbl} data-testid="plans-table">
          <thead><tr><th>Plan</th><th>Tier</th><th>Sucursales máx</th><th>Usuarios máx</th><th>Módulos</th><th>Q/mes</th></tr></thead>
          <tbody>
            {(plans.data?.items || []).map((p) => (
              <tr key={p.code} data-testid={`plan-${p.code}`}>
                <td><strong>{p.name}</strong></td><td>{p.availability_tier}</td>
                <td>{p.max_branches}</td><td>{p.max_users}</td>
                <td>{p.modules.map((mod) => <span key={mod} style={{ ...badge, marginRight: 4 }}>{mod.replace('mod_', '')}</span>)}</td>
                <td>Q{p.monthly_price_gtq}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </section>
  );
}

// TenantDetail: drawer operable de un cliente. Carga su estado fresco vía
// GET /tenants/{id} (sucursales/usuarios/uso materializados) y expone las cuatro
// operaciones de ciclo de vida: cambiar plan (licencia), editar branding,
// suspender/reactivar/terminar. Cada mutación reenvía CSRF y confirma destructivas.
function TenantDetail({
  tenant, plans, csrf, canLicense, canBranding, canStatus, onChanged, onClose,
}: {
  tenant: TenantView;
  plans: Plan[];
  csrf: string;
  canLicense: boolean;
  canBranding: boolean;
  canStatus: boolean;
  onChanged: () => void;
  onClose: () => void;
}) {
  // Estado vivo del cliente (re-fetch tras cada mutación para reflejar features/estado).
  const detail = useQuery<{ tenant: TenantView }>({
    queryKey: ['pf-tenant', tenant.id],
    queryFn: () => apiFetch(`${API}/portal/platform/tenants/${tenant.id}`),
    initialData: { tenant },
  });
  const t = detail.data?.tenant ?? tenant;

  // Inspección real del cliente: sucursales, usuarios y uso. Cada una degrada a un
  // estado muted si falla o viene vacía (un tenant nuevo trae 'Sede Central', 0 uso).
  const branchesQ = useQuery<{ items: TenantBranchRow[] }>({
    queryKey: ['pf-tenant-branches', tenant.id],
    queryFn: () => apiFetch(`${API}/portal/platform/tenants/${tenant.id}/branches`),
  });
  const usersQ = useQuery<{ items: TenantUserRow[] }>({
    queryKey: ['pf-tenant-users', tenant.id],
    queryFn: () => apiFetch(`${API}/portal/platform/tenants/${tenant.id}/users`),
  });
  const usageQ = useQuery<TenantUsage>({
    queryKey: ['pf-tenant-usage', tenant.id],
    queryFn: () => apiFetch(`${API}/portal/platform/tenants/${tenant.id}/usage`),
  });

  const refresh = () => {
    detail.refetch();
    branchesQ.refetch();
    usersQ.refetch();
    usageQ.refetch();
    onChanged();
  };

  const plan = plans.find((p) => p.code === t.plan_code);
  const features = Object.entries(t.features).filter(([, on]) => on).map(([k]) => k);

  const branches = branchesQ.data?.items;
  const users = usersQ.data?.items;
  const usage = usageQ.data?.usage;
  // Conteo real de sucursales = filas inspeccionadas; cae al materializado mientras carga.
  const branchCount = branches?.length ?? t.branch_count;

  // Alta de sucursal POR LA PLATAFORMA (el operador ya no crea sedes; se aprovisiona aquí).
  const [newBranch, setNewBranch] = useState({ name: '', address: '' });
  const createBranch = useMutation({
    mutationFn: (body: { name: string; address: string }) =>
      apiFetch(`${API}/portal/platform/tenants/${t.id}/branches`, 'POST', { headers: { 'X-CSRF-Token': csrf }, body }),
    onSuccess: () => { setNewBranch({ name: '', address: '' }); refresh(); },
  });

  // --- cambiar plan (licencia) ---
  const [planCode, setPlanCode] = useState(t.plan_code);
  const [lastFeatures, setLastFeatures] = useState<string[] | null>(null);
  const changePlan = useMutation({
    mutationFn: (code: string): Promise<LicenseResult> =>
      apiFetch(`${API}/portal/platform/tenants/${t.id}/license`, 'PATCH', {
        // REQ-20: preservar los overrides del tenant al cambiar de plan (antes se reseteaban).
        headers: { 'X-CSRF-Token': csrf }, body: { plan_code: code, module_overrides: t.module_overrides ?? {} },
      }),
    onSuccess: (res) => {
      setLastFeatures(Object.entries(res.features).filter(([, on]) => on).map(([k]) => k));
      refresh();
    },
  });
  const applyPlan = () => {
    if (planCode === t.plan_code) return;
    if (!window.confirm(`¿Cambiar el plan de "${t.brand_name}" a "${planCode}"? Se re-materializan los módulos.`)) return;
    changePlan.mutate(planCode);
  };

  // --- REQ-20: editor de features por módulo (plan base + override por tenant) ---
  // Catálogo = unión de módulos de todos los planes + los del plan/feature/override del tenant.
  const allModules = Array.from(new Set([
    ...plans.flatMap((p) => p.modules), ...(t.plan_modules ?? []),
    ...Object.keys(t.features), ...Object.keys(t.module_overrides ?? {}),
  ])).sort();
  const [ov, setOv] = useState<Record<string, boolean>>(t.module_overrides ?? {});
  const planHas = (m: string) => (t.plan_modules ?? []).includes(m);
  const effectiveOf = (m: string) => (m in ov ? ov[m] : planHas(m));
  const toggleFeature = (m: string) => {
    const next = !effectiveOf(m);
    setOv((prev) => {
      const n = { ...prev };
      if (next === planHas(m)) delete n[m]; // coincide con el plan → sin override
      else n[m] = next;                     // difiere del plan → override explícito
      return n;
    });
  };
  const resetFeature = (m: string) => setOv((prev) => { const n = { ...prev }; delete n[m]; return n; });
  const featuresDirty = JSON.stringify(ov) !== JSON.stringify(t.module_overrides ?? {});
  const saveFeatures = useMutation({
    mutationFn: (): Promise<LicenseResult> =>
      apiFetch(`${API}/portal/platform/tenants/${t.id}/license`, 'PATCH', {
        headers: { 'X-CSRF-Token': csrf }, body: { plan_code: t.plan_code, module_overrides: ov },
      }),
    onSuccess: refresh,
  });

  // --- branding white-label ---
  const [brand, setBrand] = useState<TenantBranding>({
    brand_name: t.brand_name, primary_hex: '#3a4a90', logo_ref: '',
  });
  const saveBranding = useMutation({
    mutationFn: (b: TenantBranding) =>
      apiFetch(`${API}/portal/platform/tenants/${t.id}/branding`, 'PUT', {
        headers: { 'X-CSRF-Token': csrf }, body: b,
      }),
    onSuccess: refresh,
  });
  const applyBranding = () => {
    if (!window.confirm(`¿Aplicar branding a "${t.brand_name}"? Afecta el white-label de cmo-web.`)) return;
    saveBranding.mutate(brand);
  };

  // --- ciclo de vida (suspender / reactivar / terminar) ---
  const setStatus = useMutation({
    mutationFn: (status: string) =>
      apiFetch(`${API}/portal/platform/tenants/${t.id}/status`, 'POST', {
        headers: { 'X-CSRF-Token': csrf }, body: { status },
      }),
    onSuccess: refresh,
  });
  const lifecycle = (status: string, verb: string, destructive = false) => () => {
    const warn = destructive
      ? ` Los datos NO se destruyen, pero el acceso del cliente se corta.`
      : ` Suspender bloquea el login del cliente sin destruir datos.`;
    if (!window.confirm(`¿${verb} "${t.brand_name}"?${status === 'active' ? '' : warn}`)) return;
    setStatus.mutate(status);
  };

  useEffect(() => { setPlanCode(t.plan_code); setBrand((b) => ({ ...b, brand_name: t.brand_name })); }, [t.plan_code, t.brand_name]);
  // Re-sincroniza los overrides editables cuando llega el estado fresco del tenant (tras guardar).
  useEffect(() => { setOv(t.module_overrides ?? {}); }, [JSON.stringify(t.module_overrides)]);

  const busy = changePlan.isPending || saveBranding.isPending || setStatus.isPending;

  return (
    <div style={drawer} data-testid="tenant-detail">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <strong data-testid="detail-brand">{t.brand_name} <small style={{ color: 'var(--lumal-muted)' }}>· {t.slug}</small></strong>
        <span style={statusStyle(t.license_status)} data-testid="detail-status">{t.license_status}</span>
        <button style={btnSecondary} onClick={onClose} data-testid="detail-close">Cerrar</button>
      </div>

      {/* Uso / sucursales / usuarios inspeccionados en vivo del schema del cliente. */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '0.75rem 0' }}>
        <Stat label="Sucursales" value={branchCount} testid="detail-branches" />
        <Stat label="Sucursales máx" value={plan?.max_branches} testid="detail-branch-limit" />
        <Stat label="Usuarios máx" value={plan?.max_users} testid="detail-user-limit" />
        <Stat label="Módulos activos" value={features.length} testid="detail-modules" />
      </div>

      <h4 style={{ margin: '0.5rem 0 0.25rem' }}>Uso</h4>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '0 0 0.75rem' }}>
        <Stat label="Pacientes" value={usage?.patients} testid="detail-usage-patients" />
        <Stat label="Facturas" value={usage?.invoices} testid="detail-usage-invoices" />
        <Stat label="Tickets abiertos" value={usage?.open_tickets} testid="detail-usage-open_tickets" />
        <Stat label="Grants activos" value={usage?.active_grants} testid="detail-usage-active_grants" />
      </div>
      {usageQ.isError && <p style={{ color: 'var(--lumal-muted)', fontSize: '0.82rem', margin: '0 0 0.5rem' }}>uso no disponible</p>}

      <h4 style={{ margin: '0.5rem 0 0.25rem' }}>Sucursales</h4>
      {branchesQ.isLoading ? (
        <p style={{ color: 'var(--lumal-muted)', fontSize: '0.82rem' }}>cargando…</p>
      ) : branchesQ.isError ? (
        <p style={{ color: 'var(--lumal-muted)', fontSize: '0.82rem' }}>sucursales no disponibles</p>
      ) : !branches || branches.length === 0 ? (
        <p style={{ color: 'var(--lumal-muted)', fontSize: '0.82rem' }}>sin sucursales</p>
      ) : (
        <table style={tbl} data-testid="detail-branch-list">
          <tbody>
            {branches.map((b) => (
              <tr key={b.id} data-testid="detail-branch-row">
                <td><strong>{b.name}</strong><br /><small style={{ color: 'var(--lumal-muted)' }}>{b.address || '—'}</small></td>
                <td style={{ textAlign: 'right' }}>
                  <span style={statusStyle(b.active ? 'active' : 'suspended')}>{b.active ? 'activa' : 'inactiva'}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {canStatus && (
        <form data-testid="pf-branch-create" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}
          onSubmit={(e) => { e.preventDefault(); if (newBranch.name.trim()) createBranch.mutate(newBranch); }}>
          <input placeholder="Nueva sede: nombre" value={newBranch.name} required data-testid="pf-branch-name"
            onChange={(e) => setNewBranch({ ...newBranch, name: e.target.value })} style={{ flex: 1, minWidth: 130 }} />
          <input placeholder="Dirección (opcional)" value={newBranch.address} data-testid="pf-branch-address"
            onChange={(e) => setNewBranch({ ...newBranch, address: e.target.value })} style={{ flex: 1, minWidth: 130 }} />
          <button type="submit" style={btnPrimary} disabled={createBranch.isPending} data-testid="pf-branch-submit">Crear sede</button>
          {createBranch.isError && <Banner variant="warn">{(createBranch.error as any)?.message_safe ?? 'No se pudo crear la sede'}</Banner>}
        </form>
      )}

      <h4 style={{ margin: '0.75rem 0 0.25rem' }}>
        Usuarios {users && users.length > 0 ? <small style={{ color: 'var(--lumal-muted)' }}>· {users.length}</small> : null}
      </h4>
      {usersQ.isLoading ? (
        <p style={{ color: 'var(--lumal-muted)', fontSize: '0.82rem' }}>cargando…</p>
      ) : usersQ.isError ? (
        <p style={{ color: 'var(--lumal-muted)', fontSize: '0.82rem' }}>usuarios no disponibles</p>
      ) : !users || users.length === 0 ? (
        <p style={{ color: 'var(--lumal-muted)', fontSize: '0.82rem' }}>sin usuarios</p>
      ) : (
        <table style={tbl} data-testid="detail-user-list">
          <tbody>
            {users.map((u) => (
              <tr key={`${u.user_sub}:${u.branch_id}`}>
                <td><code style={{ fontSize: '0.78rem' }}>{u.user_sub}</code></td>
                <td style={{ textAlign: 'right' }}><small style={{ color: 'var(--lumal-muted)' }}>{u.branch_id}</small></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h4 style={{ margin: '0.75rem 0 0.25rem' }}>Módulos materializados</h4>
      <div data-testid="detail-features" style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {features.length === 0
          ? <em style={{ color: 'var(--lumal-muted)' }}>sin módulos</em>
          : features.map((f) => <span key={f} style={badge} data-testid={`feature-${f}`}>{f.replace('mod_', '')}</span>)}
      </div>

      {/* (4) Cambiar plan → license endpoint, mostrando features materializadas. */}
      {canLicense && (
        <div style={{ marginTop: '0.9rem' }} data-testid="detail-plan-form">
          <h4 style={{ margin: '0 0 0.25rem' }}>Plan / licencia</h4>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={planCode} onChange={(e) => setPlanCode(e.target.value)} data-testid="detail-plan-select">
              {plans.map((p) => <option key={p.code} value={p.code}>{p.name} ({p.code})</option>)}
            </select>
            <button style={btnPrimary} onClick={applyPlan} disabled={busy || planCode === t.plan_code} data-testid="detail-plan-apply">
              Cambiar plan
            </button>
            {changePlan.isError && <Banner variant="warn">{(changePlan.error as any)?.message_safe ?? 'Error al cambiar plan'}</Banner>}
          </div>
          {lastFeatures && (
            <p style={{ fontSize: '0.82rem', marginTop: 6 }} data-testid="detail-plan-result">
              Features tras el cambio: {lastFeatures.map((f) => f.replace('mod_', '')).join(', ') || '—'}
            </p>
          )}
        </div>
      )}

      {/* REQ-20: editor de features por módulo (plan base + override por tenant). */}
      {canLicense && (
        <div style={{ marginTop: '0.9rem' }} data-testid="detail-feature-editor">
          <h4 style={{ margin: '0 0 0.25rem' }}>Features (módulos) del cliente</h4>
          <p style={{ fontSize: '0.78rem', color: 'var(--lumal-muted)', margin: '0 0 6px' }}>
            Cada módulo viene del plan; puedes forzarlo ON/OFF para este cliente (override). "↺ plan" quita el override.
          </p>
          <table data-testid="detail-feature-toggles" style={{ borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th style={{ padding: '2px 10px 2px 0' }}>Módulo</th>
                <th style={{ padding: '2px 10px' }}>Plan</th>
                <th style={{ padding: '2px 10px' }}>Efectivo</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {allModules.map((m) => {
                const overridden = m in ov;
                const eff = effectiveOf(m);
                return (
                  <tr key={m} data-testid={`feat-row-${m}`}>
                    <td style={{ padding: '2px 10px 2px 0' }}>{m.replace('mod_', '')}</td>
                    <td style={{ padding: '2px 10px', color: 'var(--lumal-muted)' }}>{planHas(m) ? 'ON' : 'OFF'}</td>
                    <td style={{ padding: '2px 10px' }}>
                      <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                        <input type="checkbox" checked={eff} onChange={() => toggleFeature(m)} data-testid={`feat-toggle-${m}`} />
                        {eff ? 'ON' : 'OFF'}{overridden ? <em style={{ color: 'var(--lumal-accent)' }}> (override)</em> : null}
                      </label>
                    </td>
                    <td>{overridden && (
                      <button style={btnSecondary} data-testid={`feat-reset-${m}`} onClick={() => resetFeature(m)}>↺ plan</button>
                    )}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <button style={{ ...btnPrimary, marginTop: 8 }} data-testid="detail-feature-save"
            disabled={busy || saveFeatures.isPending || !featuresDirty}
            onClick={() => { if (window.confirm(`¿Guardar features de "${t.brand_name}"?`)) saveFeatures.mutate(); }}>
            {saveFeatures.isPending ? 'Guardando…' : 'Guardar features'}
          </button>
          {saveFeatures.isError && <Banner variant="warn">{(saveFeatures.error as any)?.message_safe ?? 'Error al guardar features'}</Banner>}
        </div>
      )}

      {/* (2) Editar branding → branding endpoint, con confirmación. */}
      {canBranding && (
        <div style={{ marginTop: '0.9rem' }} data-testid="detail-branding-form">
          <h4 style={{ margin: '0 0 0.25rem' }}>Branding (white-label)</h4>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input placeholder="Nombre comercial" value={brand.brand_name}
              onChange={(e) => setBrand({ ...brand, brand_name: e.target.value })} data-testid="branding-name" />
            <label style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4 }}>
              Color
              <input type="color" value={brand.primary_hex}
                onChange={(e) => setBrand({ ...brand, primary_hex: e.target.value })} data-testid="branding-color" />
            </label>
            <input placeholder="logo_ref (ruta/asset)" value={brand.logo_ref}
              onChange={(e) => setBrand({ ...brand, logo_ref: e.target.value })} data-testid="branding-logo" />
            <button style={btnPrimary} onClick={applyBranding} disabled={busy} data-testid="branding-apply">Guardar branding</button>
            {saveBranding.isSuccess && <span style={{ color: 'var(--lumal-success)', fontSize: '0.8rem' }} data-testid="branding-saved">✓ guardado</span>}
            {saveBranding.isError && <Banner variant="warn">{(saveBranding.error as any)?.message_safe ?? 'Error al guardar branding'}</Banner>}
          </div>
        </div>
      )}

      {/* (3) Suspender / reactivar / terminar → status endpoint, con window.confirm. */}
      {canStatus && (
        <div style={{ marginTop: '0.9rem' }} data-testid="detail-lifecycle">
          <h4 style={{ margin: '0 0 0.25rem' }}>Ciclo de vida</h4>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {t.license_status === 'active' || t.license_status === 'trial' ? (
              <button style={btnSecondary} onClick={lifecycle('suspended', 'Suspender')} disabled={busy} data-testid="lifecycle-suspend">
                Suspender
              </button>
            ) : (
              <button style={btnSecondary} onClick={lifecycle('active', 'Reactivar')} disabled={busy} data-testid="lifecycle-reactivate">
                Reactivar
              </button>
            )}
            {t.license_status !== 'terminated' && (
              <button style={btnDanger} onClick={lifecycle('terminated', 'Terminar', true)} disabled={busy} data-testid="lifecycle-terminate">
                Terminar
              </button>
            )}
            {setStatus.isError && <Banner variant="warn">{(setStatus.error as any)?.message_safe ?? 'Error al cambiar estado'}</Banner>}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid }: { label: string; value?: number; testid: string }) {
  return (
    <div style={statCard}>
      <div style={statNumber} data-testid={testid}>{value ?? '—'}</div>
      <div style={{ color: 'var(--lumal-muted)', fontSize: '0.85rem' }}>{label}</div>
    </div>
  );
}

function statusStyle(status: string): React.CSSProperties {
  const map: Record<string, string> = {
    active: 'var(--lumal-success)', suspended: 'var(--lumal-warn)',
    terminated: 'var(--lumal-danger)', trial: 'var(--lumal-accent)',
  };
  return { ...badge, color: map[status] ?? 'var(--lumal-muted)', borderColor: map[status] ?? 'var(--lumal-border)' };
}
